import "./style.scss";

import InfoIcon from "@mui/icons-material/Info";
import CurrencyIcon from "components/shared/CurrencyIcon";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import { onHighlightField } from "components/TitleFormContainer/utils";
import type { ITermFormData } from "containers/TermFormContainer";
import { SmallBody } from "helpers/TypographyComponents";
import { formatNumber } from "helpers/utils";
import { useTranslation } from "react-i18next";

interface IOpeningBalanceField {
    booleanValues: Record<string, boolean | any>;
    chosenAdvance: string | number;
    useFormData: ITermFormData;
}

/**
 * Opening balance field — affects tpd.balance directly, independent of advance payments.
 */
const OpeningBalanceField = ({ booleanValues, chosenAdvance, useFormData }: IOpeningBalanceField) => {
    const [t] = useTranslation();
    const { control, errors } = useFormData;
    const { usesTemplate, termExistOnClosePeriod } = booleanValues;
    const formattedChosenAdvance = +chosenAdvance;

    return (
        <div className="advance-section-container">
            <div className="advance-container">
                <NumberFormComponent
                    sx={onHighlightField(usesTemplate && chosenAdvance !== "")}
                    label={t("ConnectTitle.Deposit")}
                    control={control}
                    controlName="advancePayment"
                    placeholder="0"
                    error={errors.advancePayment}
                    float={true}
                    disabled={termExistOnClosePeriod}
                    disabledText={t("ConnectTitle.AdvanceDisabled")}
                    icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                    acceptsNegative
                    helpdeskInfo={{
                        text: termExistOnClosePeriod
                            ? t("Documentation.ChangeAdvance")
                            : t("Documentation.UnderstandBalance"),
                        link: termExistOnClosePeriod
                            ? "https://helpdesk.crealo.app/fr/article/je-narrive-pas-a-modifier-le-solde-dun-contrat-comment-faire-kaxfcd/"
                            : "https://helpdesk.crealo.app/fr/article/comment-remplir-le-champ-solde-du-contrat-135p0sx/?bust=1712759820770",
                    }}
                />

                {formattedChosenAdvance === 0 ? (
                    <SmallBody className="advance-hint" color="grey">
                        {t("ConnectTitle.AdvanceInfo")}
                    </SmallBody>
                ) : !errors.advancePayment && formattedChosenAdvance > 0 ? (
                    <span className="advance-info">
                        <InfoIcon className="advance-info-icon" />
                        {t("ConnectTitle.BalanceMessagePositive", {
                            amount: formatNumber(formattedChosenAdvance, { isCurrency: true }),
                        })}
                    </span>
                ) : (
                    !errors.advancePayment && (
                        <span className="advance-info">
                            <InfoIcon className="advance-info-icon" />
                            {t("ConnectTitle.BalanceMessageNegative", {
                                amount: formatNumber(formattedChosenAdvance, { isCurrency: true }),
                            })}
                        </span>
                    )
                )}
            </div>
        </div>
    );
};

export default OpeningBalanceField;
